import { useRef, useState } from "react";
import { UploadCloud, FileText, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import SectionHeader from "../components/common/SectionHeader";
import { app } from "../application";
import { getEngine } from "../config/engines";
import { DEFAULT_VERTICAL_ID, DEFAULT_ORGANIZATION_ID } from "../config/vertical";
import { parseCSV, rowsFromSheetArray, buildActorDraftsFromRows } from "./ingestionParsing";
import { T } from "../lib/testids";

// Nombre de hoja preferido para archivos .xlsx de laboratorio (ej.
// Commercial_Operator_Lote_Prueba_Renovaciones.xlsx) — si el archivo la
// tiene, se usa esa exactamente; si no, se usa la primera hoja del libro
// y se avisa cuál, para que nunca se lea en silencio la hoja equivocada
// (ej. "Instrucciones_QA" o "Registro_Resultados" del mismo archivo).
const PREFERRED_XLSX_SHEET = "Lote_Prueba";

// Fricción mínima de carga: la plantilla visible solo pide lo que
// Renovaciones necesita para empezar. industry_segment/total_billed_usd/
// months_inactive siguen existiendo en ingestionParsing.js (compatibilidad
// hacia atrás con cargas más completas), pero ya no se piden ni se
// muestran como parte de la experiencia inicial.
const SAMPLE_HEADERS = "company_name,contact_name,phone,renewal_date,email";
const SAMPLE_ROWS = [
  "Grupo Andalucía S.A.,Carlos Ramírez,+52 55 1234 5678,Ago-2026,carlos.ramirez@andalucia.com",
  "Corporativo Monterrey S.A.S.,María González,+52 81 8765 4321,Sep-2026,maria.gonzalez@monterrey.com",
  "Distribuidora del Valle S.A.,Diego Torres,+52 33 2233 4455,Oct-2026,diego.torres@delvalle.com",
];

export default function Ingestion() {
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const handleFile = (f) => {
    if (!f) return;
    setFile(f);
    const isExcel = /\.xlsx?$/i.test(f.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      let parsed;
      if (isExcel) {
        const workbook = XLSX.read(e.target.result, { type: "array" });
        const sheetName = workbook.SheetNames.includes(PREFERRED_XLSX_SHEET)
          ? PREFERRED_XLSX_SHEET
          : workbook.SheetNames[0];
        const sheetArray = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], {
          header: 1,
          defval: "",
        });
        parsed = rowsFromSheetArray(sheetArray);
        if (sheetName !== PREFERRED_XLSX_SHEET) {
          toast.info(`Leyendo la hoja "${sheetName}" (no encontré una hoja llamada "${PREFERRED_XLSX_SHEET}")`);
        }
      } else {
        parsed = parseCSV(e.target.result);
      }
      setRows(parsed);
      if (!parsed.length) toast.error("El archivo no contiene filas válidas");
      else toast.success(`${parsed.length} filas detectadas`);
    };
    if (isExcel) reader.readAsArrayBuffer(f);
    else reader.readAsText(f);
  };

  const onDrop = (e) => {
    e.preventDefault();
    handleFile(e.dataTransfer.files?.[0]);
  };

  const doUpload = async () => {
    if (!rows.length) return;
    setUploading(true);
    try {
      const { drafts, dateWarnings, duplicateWarnings, crossSellFormatWarnings } = buildActorDraftsFromRows(rows, {
        organizationId: DEFAULT_ORGANIZATION_ID,
        verticalId: DEFAULT_VERTICAL_ID,
      });
      const res = await app.ingestion.ingestActorDrafts(drafts);
      setResult({ ...res, dateWarnings, duplicateWarnings, crossSellFormatWarnings });
      toast.success(`${res.actorsCreated} cuentas importadas`);
    } catch (e) {
      toast.error("Error al procesar");
    }
    setUploading(false);
  };

  const downloadSample = () => {
    // BOM + charset explícito: sin esto, Excel abre el CSV asumiendo la
    // codificación local y los acentos se ven como "AndalucÃ­a" — es un
    // problema de cómo se declara el archivo, no de los datos en sí.
    const csv = "﻿" + [SAMPLE_HEADERS, ...SAMPLE_ROWS].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "commercial-operator-cartera-ejemplo.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="ambient-bg min-h-full px-4 sm:px-8 py-6 sm:py-8">
      <SectionHeader
        overline="Tu cartera"
        title="Cargar cartera"
        subtitle="Sube un archivo CSV o Excel (.xlsx) con tu cartera de clientes. Con esa información, Commercial Operator organiza tus renovaciones y prioridades de trabajo en un solo lugar."
      />

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
        {/* CSV */}
        <div>
          <div
            data-testid={T.ingestDropzone}
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            className="border-2 border-dashed border-[#1b2846] hover:border-amber-500 bg-[#0d1321] p-6 sm:p-12 flex flex-col items-center justify-center text-center cursor-pointer transition-colors"
          >
            <UploadCloud size={32} className="text-zinc-500 mb-4" strokeWidth={1.5} />
            <div className="font-display font-bold text-base text-zinc-100">
              Arrastra tu CSV o Excel aquí
            </div>
            <div className="text-xs text-zinc-500 mt-1">o haz click para seleccionar</div>
            <input
              data-testid={T.ingestFileInput}
              ref={inputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
          </div>

          {file && (
            <div className="mt-4 border border-[#17223a] bg-[#0f1524] p-4 flex flex-wrap items-center gap-3">
              <div className="w-10 h-10 border border-[#1b2846] flex items-center justify-center shrink-0">
                <FileText size={18} className="text-amber-400" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-zinc-100 font-medium truncate">{file.name}</div>
                <div className="text-[10px] text-zinc-500 font-mono uppercase tracking-[0.15em] mt-0.5">
                  {rows.length} filas · {(file.size / 1024).toFixed(1)} KB
                </div>
              </div>
              <button
                data-testid={T.ingestProcess}
                onClick={doUpload}
                disabled={uploading || !rows.length}
                className="w-full sm:w-auto text-[11px] font-mono uppercase tracking-[0.15em] px-4 py-2.5 sm:py-2 bg-amber-500 text-black hover:bg-amber-400 btn-glow-amber disabled:opacity-50"
              >
                {uploading ? "Analizando tu cartera..." : "Procesar cartera"}
              </button>
            </div>
          )}

          {result && (
            <div className="mt-4 border border-emerald-500/40 bg-emerald-500/5 p-4">
              <div className="flex flex-wrap items-start gap-3">
                <CheckCircle2 size={18} className="text-emerald-400 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-[200px]">
                  <div className="text-sm text-emerald-300 font-medium">
                    Terminamos de revisar tu cartera.
                  </div>
                  <div className="text-xs text-zinc-400 mt-1">
                    {result.actorsCreated} cuentas cargadas — así quedó organizado el trabajo:
                  </div>
                  <div className="text-xs text-zinc-400 mt-2 space-y-0.5">
                    <div>{getEngine("renewal").humanLabel}: {result.opportunitiesByEngine?.renewal || 0}</div>
                    <div>{getEngine("recovery").humanLabel}: {result.opportunitiesByEngine?.recovery || 0}</div>
                    <div>{getEngine("cross_sell").humanLabel}: {result.opportunitiesByEngine?.cross_sell || 0}</div>
                    <div className="text-zinc-600">
                      {result.actorsCreated -
                        (result.opportunitiesByEngine?.renewal || 0) -
                        (result.opportunitiesByEngine?.recovery || 0) -
                        (result.opportunitiesByEngine?.cross_sell || 0)}{" "}
                      cuentas siguen en tu radar, sin acción todavía
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => navigate("/")}
                  className="w-full sm:w-auto text-[11px] font-mono uppercase tracking-[0.15em] px-3 py-2 sm:py-1.5 border border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/10 shrink-0"
                >
                  Ver en Hoy
                </button>
              </div>

              {result.warnings?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-emerald-500/20 text-xs text-zinc-500">
                  {result.warnings.length} fila(s) rechazada(s): {result.warnings.join("; ")}
                </div>
              )}

              {result.dateWarnings?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-amber-500/20">
                  <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-amber-400 mb-1.5">
                    Meses a revisar
                  </div>
                  {result.dateWarnings.map((w, i) => (
                    <div key={i} className="text-xs text-zinc-400 mb-1">
                      Fila {w.row} — {w.field}: "{w.value}". {w.suggestion}
                    </div>
                  ))}
                </div>
              )}

              {result.duplicateWarnings?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-amber-500/20">
                  <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-amber-400 mb-1.5">
                    Posibles duplicados — tú decides si son la misma cuenta
                  </div>
                  {result.duplicateWarnings.map((d, i) => (
                    <div key={i} className="text-xs text-zinc-400 mb-1">
                      Filas {d.rows.join(" y ")} — "{d.companyName}" coinciden por {d.matchedBy}.
                    </div>
                  ))}
                </div>
              )}

              {result.crossSellFormatWarnings?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-amber-500/20">
                  <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-amber-400 mb-1.5">
                    Potencial de venta cruzada con formato inválido
                  </div>
                  {result.crossSellFormatWarnings.map((w, i) => (
                    <div key={i} className="text-xs text-zinc-400 mb-1">
                      Fila {w.row} — {w.field}: "{w.value}". {w.suggestion}
                    </div>
                  ))}
                </div>
              )}

              {result.crossSellSkipped?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-[#1b2846]">
                  <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500 mb-1.5">
                    Sin oportunidad de venta cruzada
                  </div>
                  {result.crossSellSkipped.map((s, i) => (
                    <div key={i} className="text-xs text-zinc-500 mb-1">
                      {s.companyName}: {s.reasons.join(" · ")}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

        </div>

        <aside className="space-y-4">
          <div className="border border-[#17223a] bg-[#0d1321] p-5">
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-500 mb-2">
              Qué necesita el archivo
            </div>

            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-amber-400/80 mb-1">
              Para empezar
            </div>
            <ul className="space-y-1 text-xs text-zinc-300 mb-3">
              <li>
                Empresa
                <div className="text-[10px] text-zinc-600">Encabezado en el archivo: <code className="text-zinc-500">company_name</code></div>
              </li>
              <li>
                Nombre del contacto
                <div className="text-[10px] text-zinc-600">Encabezado en el archivo: <code className="text-zinc-500">contact_name</code></div>
              </li>
              <li>
                Teléfono / WhatsApp
                <div className="text-[10px] text-zinc-600">Encabezado en el archivo: <code className="text-zinc-500">phone</code></div>
              </li>
              <li>
                Mes de renovación
                <div className="text-[10px] text-zinc-600">Encabezado en el archivo: <code className="text-zinc-500">renewal_date</code></div>
                <div className="text-[10px] text-zinc-600 mt-0.5">
                  Ejemplos válidos: <code className="text-zinc-500">Ago-2026</code> ·{" "}
                  <code className="text-zinc-500">Sep-2026</code> ·{" "}
                  <code className="text-zinc-500">Oct-2026</code>
                </div>
              </li>
            </ul>

            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500 mb-1">
              Opcional
            </div>
            <ul className="space-y-1 text-xs text-zinc-400">
              <li>
                Correo
                <div className="text-[10px] text-zinc-600">Encabezado en el archivo: <code className="text-zinc-500">email</code></div>
              </li>
              <li>
                Facturación anual
                <div className="text-[10px] text-zinc-600">Encabezado en el archivo: <code className="text-zinc-500">total_billed_usd</code></div>
                <div className="text-[10px] text-zinc-600 mt-0.5">
                  Sin este dato, el valor estimado de cada oportunidad se muestra como "Lo estoy calculando" en vez de un monto — Commercial Operator no inventa una cifra sin base real.
                </div>
              </li>
            </ul>

            <p className="text-[10px] text-zinc-600 mt-3 leading-relaxed">
              Con estos cuatro datos ya puedes empezar. Basta con el mes en que tu
              cliente renueva — no necesitas el día exacto. Si un cliente no tiene una
              renovación próxima, deja ese mes vacío — igual quedará cargado en tu
              cartera, solo no aparecerá en Renovaciones.
            </p>
            <button
              data-testid={T.ingestDownloadTemplate}
              onClick={downloadSample}
              className="w-full mt-4 text-[11px] font-mono uppercase tracking-[0.15em] px-3 py-2 border border-[#1b2846] text-zinc-300 hover:border-amber-500 hover:text-amber-400"
            >
              Descargar plantilla
            </button>
          </div>

          <div className="border border-amber-500/30 bg-amber-500/5 p-5">
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-amber-400 mb-2">
              Qué pasa con tu cartera
            </div>
            <p className="text-xs text-zinc-400 leading-relaxed">
              Tu cartera quedará guardada en tu espacio de Commercial Operator. Al
              procesarla, verás tus clientes, renovaciones y prioridades de trabajo
              organizados en tu Centro de Operaciones.
            </p>
            <p className="text-xs text-zinc-300 leading-relaxed mt-2 font-medium">
              Procesar tu cartera no enviará mensajes. Primero podrás revisar las
              renovaciones y prioridades detectadas.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}
